import { useState } from "react";
import DonutChart from "../../components/DonutChart";
import { Shades } from "../../utils/common";
import { CardHeader, KpiTile, ThresholdPill, ThresholdSplitCard, DonutSplitCard } from "./Common";
import MuiCallsTable from "./MuiCallsTable";

const sumTotalCalls = (arr = []) => arr.reduce((sum, d) => sum + d.total_calls, 0);
const safeAvg = (sum = 0, count = 0) => (count > 0 ? sum / count : 0);
const pct = (part = 0, whole = 0) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export default function CallSection({
  sectionTitle,
  durationTitle,
  avgDurationTitle,
  attendeeChartTitle,
  attendeeLabelKey,
  statusChartTitle,
  tableTitle,
  data,
  columns,
  pagination, // { page, limit, total } — page is 1-indexed
  onPaginationModelChange, // (model: {page, pageSize} - 0-indexed) => void
  tableLoadingKey,
  tableLoading,
  chartLoading,
  formatDuration,
  exportFileName,
  onThresholdChange, // (newThreshold: number) => void — wired to the ghlThreshold/kixieThreshold quick filter
}) {
  const threshold = data.duration_threshold || 0;
  const belowLabel = `Below ${threshold}s`;
  const aboveLabel = `Above ${threshold}s`;

  const [thresholdDraft, setThresholdDraft] = useState(String(threshold));

  const totalCalls = data.total_calls || 0;
  const completionRate = pct(data.calls_with_duration, totalCalls);
  const belowPct = pct(data.calls_below_threshold, totalCalls);
  const abovePct = pct(data.calls_above_threshold, totalCalls);

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl shadow-sm my-4 mx-auto max-w-[1600px] p-1 sm:p-4">
      <div className="flex items-center gap-2 mb-4">
        <p className="text-xl font-semibold text-gray-700 text-left">{sectionTitle}</p>
        {onThresholdChange && (
          <ThresholdPill
            value={threshold}
            draft={thresholdDraft}
            onDraftChange={setThresholdDraft}
            onApply={(newValue) => {
              setThresholdDraft(String(newValue));
              onThresholdChange(newValue);
            }}
          />
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {/* <div className="grid grid-cols-1 col-span-1 gap-4 mb-4">
          <KpiTile
            title={durationTitle}
            value={formatDuration(data.total_duration)}
            // subtext={`${belowLabel} / ${aboveLabel}`}
          />
          <KpiTile
            title={avgDurationTitle}
            value={formatDuration(data.avg_duration)}
            // subtext="per call"
          />
        </div> */}

        {/* Duration threshold breakdown — 2 cards instead of 4 raw boxes */}
        <div className="grid grid-cols-1 col-span-1 gap-4 mb-4">
          <ThresholdSplitCard title={durationTitle} value={formatDuration(data.total_duration)} belowLabel={belowLabel} belowValue={formatDuration(data.duration_below_threshold_sum)} aboveLabel={aboveLabel} aboveValue={formatDuration(data.duration_above_threshold_sum)} belowPct={belowPct} abovePct={abovePct} />
          <ThresholdSplitCard title={avgDurationTitle} value={formatDuration(data.avg_duration)} belowLabel={belowLabel} belowValue={formatDuration(safeAvg(data.duration_below_threshold_sum, data.calls_below_threshold))} aboveLabel={aboveLabel} aboveValue={formatDuration(safeAvg(data.duration_above_threshold_sum, data.calls_above_threshold))} belowPct={belowPct} abovePct={abovePct} />
        </div>

        {/* Donuts — unchanged */}
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:col-span-2 gap-4 mb-4">
          {attendeeChartTitle && (
            <DonutSplitCard title={attendeeChartTitle} belowLabel={belowLabel} belowValue={data.calls_below_threshold} aboveLabel={aboveLabel} aboveValue={data.calls_above_threshold}>
              <DonutChart data={data.calls_by_attendee?.map((d) => d.total_calls).slice(0, 5)} labels={data.calls_by_attendee?.map((d) => d[attendeeLabelKey]).slice(0, 5)} rounded={true} centerValue={sumTotalCalls(data.calls_by_attendee)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="140px" width="140px" loading={chartLoading} />
            </DonutSplitCard>
          )}

          <DonutSplitCard title={statusChartTitle} belowLabel={belowLabel} belowValue={data.calls_below_threshold} aboveLabel={aboveLabel} aboveValue={data.calls_above_threshold}>
            <DonutChart data={data.calls_by_status?.map((d) => d.total_calls).slice(0, 5)} labels={data.calls_by_status?.map((d) => d.status).slice(0, 5)} rounded={true} centerValue={sumTotalCalls(data.calls_by_status)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="140px" width="140px" loading={chartLoading} />
          </DonutSplitCard>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
        <CardHeader title={tableTitle} />
        <MuiCallsTable rows={data.calls} columns={columns} loading={!!tableLoading[tableLoadingKey]} rowCount={pagination.total} paginationModel={{ page: Math.max(0, (pagination.page || 1) - 1), pageSize: pagination.limit }} onPaginationModelChange={onPaginationModelChange} title={tableTitle} fileName={exportFileName} />
      </div>
    </div>
  );
}
